import { useState, useEffect } from 'react';
import QuizHook from './components/QuizHook';
import QuizSection from './components/QuizSection';
import TransitionScreen from './components/TransitionScreen';
import ResultSection from './components/ResultSection';
import SeoHead from '../../components/SeoHead';

export type LeakType = 'Decision Drift' | 'Focus Fragmentation' | 'Tool Chaos' | 'Busy Work Loop';
export type Severity = 'Low' | 'Moderate' | 'High' | 'Compounding';
export type Archetype = 'Reactive Operator' | 'Fragmented Focus' | 'Tool Overload' | 'Decision Drift';

export interface QuizAnswer {
  questionId: number;
  answer: string;
  leakType: LeakType;
  severity: number;
  archetypeMarkers: Archetype[];
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const homeSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: "What's Secretly Slowing Your Growth? | Founder Clarity Leak Quiz",
  description:
    '92% of founders who take this discover a hidden clarity leak. 6 questions. 30 seconds. Uncover your Leak Archetype and find out how many hours per week your decision system is costing you.',
  url: `${SITE_URL}/`,
  inLanguage: 'en',
  isPartOf: {
    '@type': 'WebSite',
    name: 'Founder Clarity Leak Quiz',
    url: `${SITE_URL}/`,
  },
  mainEntity: {
    '@type': 'Quiz',
    name: 'Founder Clarity Leak Quiz',
    description:
      'A 6-question diagnostic quiz that identifies your Clarity Leak Archetype and estimates your weekly momentum loss.',
    educationalLevel: 'Founder / Entrepreneur',
    about: {
      '@type': 'Thing',
      name: 'Decision clarity and founder productivity',
    },
  },
};

export default function Home() {
  const [stage, setStage] = useState<'hook' | 'quiz' | 'transition' | 'result'>('hook');
  const [answers, setAnswers] = useState<QuizAnswer[]>([]);
  const [result, setResult] = useState<{ 
    leakType: LeakType; 
    severity: Severity;
    archetype: Archetype;
    severityTier: number;
  } | null>(null);
  const [sessionId] = useState(() => crypto.randomUUID());

  useEffect(() => {
    // Track analytics
    if (stage === 'quiz' && answers.length === 0) {
      trackEvent('quiz_start');
    }
  }, [stage, answers.length]);

  const trackEvent = (eventName: string, properties?: Record<string, string>) => {
    // Analytics tracking placeholder
    console.log('Analytics:', eventName, properties);
  };

  const handleStart = () => {
    setStage('quiz');
  };

  const handleAnswer = (answer: QuizAnswer) => {
    const newAnswers = [...answers, answer];
    setAnswers(newAnswers);
    
    trackEvent('question_answered', {
      question: answer.questionId.toString(),
      answer: answer.answer
    });

    // Show transition screen after question 3
    if (newAnswers.length === 3) {
      setStage('transition');
      setTimeout(() => {
        setStage('quiz');
      }, 1500);
      return;
    }

    if (newAnswers.length === 6) {
      // Calculate archetype
      const archetypeCounts: Record<Archetype, number> = {
        'Reactive Operator': 0,
        'Fragmented Focus': 0,
        'Tool Overload': 0,
        'Decision Drift': 0
      };

      newAnswers.forEach(ans => {
        ans.archetypeMarkers.forEach(marker => {
          archetypeCounts[marker]++;
        });
      });

      const dominantArchetype = Object.entries(archetypeCounts).reduce((a, b) => 
        b[1] > a[1] ? b : a
      )[0] as Archetype;

      // Calculate leak type for tracking
      const leakTypeCounts: Record<LeakType, number> = {
        'Decision Drift': 0,
        'Focus Fragmentation': 0,
        'Tool Chaos': 0,
        'Busy Work Loop': 0
      };

      let totalSeverity = 0;

      newAnswers.forEach(ans => {
        leakTypeCounts[ans.leakType]++;
        totalSeverity += ans.severity;
      });

      const dominantType = Object.entries(leakTypeCounts).reduce((a, b) => 
        b[1] > a[1] ? b : a
      )[0] as LeakType;

      // Calculate severity with weighted scoring
      const avgSeverity = totalSeverity / 6;
      let severityLevel: Severity;
      let severityTier: number;
      
      if (avgSeverity < 1.5) {
        severityLevel = 'Low';
        severityTier = 1;
      } else if (avgSeverity < 2.5) {
        severityLevel = 'Moderate';
        severityTier = 2;
      } else if (avgSeverity < 3.5) {
        severityLevel = 'High';
        severityTier = 3;
      } else {
        severityLevel = 'Compounding';
        severityTier = 4;
      }

      setResult({ 
        leakType: dominantType, 
        severity: severityLevel,
        archetype: dominantArchetype,
        severityTier
      });
      setStage('result');
      
      trackEvent('quiz_complete', {
        leak_type: dominantType,
        severity: severityLevel,
        archetype: dominantArchetype
      });
    }
  };

  const handleLavooClick = () => {
    if (result) {
      trackEvent('lavoo_click', {
        leak_type: result.leakType,
        severity: result.severity,
        archetype: result.archetype
      });
      
      const url = `https://lavoo.io/?utm_source=quiz&utm_medium=referral&utm_campaign=founder_leak_type&utm_content=${encodeURIComponent(result.leakType.toLowerCase().replace(' ', '_'))}&utm_term=${result.severity.toLowerCase()}&sid=${sessionId}`;
      window.location.href = url;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="What's Secretly Slowing Your Growth? | Founder Clarity Leak Quiz"
        description="92% of founders who take this discover a hidden clarity leak. 6 questions. 30 seconds. Uncover your Leak Archetype and find out how many hours per week your decision system is costing you."
        canonicalPath="/"
        schemaJson={homeSchema}
      />
      {stage === 'hook' && <QuizHook onStart={handleStart} />}
      {stage === 'quiz' && (
        <QuizSection 
          currentQuestion={answers.length + 1} 
          onAnswer={handleAnswer}
        />
      )}
      {stage === 'transition' && <TransitionScreen />}
      {stage === 'result' && result && (
        <ResultSection 
          leakType={result.leakType}
          severity={result.severity}
          archetype={result.archetype}
          severityTier={result.severityTier}
          onLavooClick={handleLavooClick}
        />
      )}
    </div>
  );
}